import React, { useEffect, useState } from 'react';
import Layout from '../../components/shared/Layout';
import { Card, PageHeader, Button, Badge, Input } from '../../components/shared/ui';
import api, { studentAPI } from '../../utils/api';

const STATUS = {
  pending:     { label:'Bekliyor',     tone:'gray',   color:'#6b7280' },
  shortlisted: { label:'Ön Listede',  tone:'amber',  color:'#a16207' },
  invited:     { label:'Davet Edildi',tone:'blue',   color:'#1d4ed8' },
  accepted:    { label:'Kabul Edildi',tone:'green',  color:'#16a34a' },
  rejected:    { label:'Reddedildi', tone:'red',    color:'#dc2626' },
};

const Timeline = ({ appId, appliedAt }) => {
  const [history, setHistory] = useState(null);
  useEffect(() => { api.get(`/applications/${appId}/history`).then(r => setHistory(r.data)).catch(() => setHistory([])); }, [appId]);
  if (history === null) return <div className="text-xs text-ink-400 p-2">Yükleniyor...</div>;
  const events = [{ new_status:'pending', created_at: appliedAt, note:'Başvuru yapıldı' }, ...history];
  return (
    <div className="mt-3 p-3 bg-ink-50 rounded-lg border-l-4 border-brand-200">
      <div className="text-sm font-semibold text-ink-700 mb-2">Durum Geçmişi</div>
      {events.map((e,i) => {
        const st = STATUS[e.new_status] || STATUS.pending;
        return (
          <div key={i} className="flex gap-2 mb-2 items-start">
            <div className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: st.color }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink-900"><strong>{st.label}</strong>
                <span className="text-ink-400 ml-2 text-[11px]">{new Date(e.created_at).toLocaleString('tr-TR', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
              </div>
              {e.note && <div className="text-[11px] text-ink-500 mt-0.5">{e.note}</div>}
            </div>
          </div>
        );
      })}
      {events.length === 1 && <div className="text-xs text-ink-400">Henüz başka güncelleme yok.</div>}
    </div>
  );
};

const ReviewModal = ({ app, onClose }) => {
  const [form, setForm] = useState({ rating:5, mentorship_rating:5, workload_rating:5, learning_rating:5, title:'', content:'', would_recommend:true, is_anonymous:false });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/applications/${app.id}/review`).then(r => { if (r.data) setForm(r.data); }).catch(() => {}); }, [app.id]);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/applications/${app.id}/review`, form); alert('Değerlendirmeniz kaydedildi. Teşekkürler!'); onClose(); }
    catch (e) { alert(e.response?.data?.error || 'Kaydedilemedi'); }
    setSaving(false);
  };
  const Star = ({ field }) => (
    <div className="flex gap-1">{[1,2,3,4,5].map(i => (
      <span key={i} onClick={() => setForm(f => ({ ...f, [field]: i }))}
        className={`text-2xl cursor-pointer ${i <= (form[field] || 0) ? 'text-amber-500' : 'text-ink-300'}`}>★</span>
    ))}</div>
  );
  return (
    <div className="fixed inset-0 bg-ink-900/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-ink-900">Staj Değerlendirmesi</h2>
        <p className="text-sm text-ink-500 mb-4">{app.company_name} — {app.position_title}</p>
        <div className="mb-4">
          <label className="text-sm font-medium text-ink-700 block mb-2">Genel Puan *</label>
          <Star field="rating" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div><label className="text-xs text-ink-500">Mentorluk</label><Star field="mentorship_rating" /></div>
          <div><label className="text-xs text-ink-500">İş Yükü</label><Star field="workload_rating" /></div>
          <div><label className="text-xs text-ink-500">Öğrenme Fırsatı</label><Star field="learning_rating" /></div>
        </div>
        <Input placeholder="Başlık (opsiyonel)" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mb-2" />
        <textarea placeholder="Staj deneyimini anlat (opsiyonel)" value={form.content || ''} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
          rows={5} className="input resize-y mb-3" />
        <label className="flex items-center gap-2 text-sm text-ink-700 mb-2">
          <input type="checkbox" checked={!!form.would_recommend} onChange={e => setForm(f => ({ ...f, would_recommend: e.target.checked }))} />
          Bu şirketi tavsiye ederim
        </label>
        <label className="flex items-center gap-2 text-sm text-ink-700 mb-4">
          <input type="checkbox" checked={!!form.is_anonymous} onChange={e => setForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
          Anonim olarak paylaş (ismim gizlenecek)
        </label>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>İptal</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
        </div>
      </Card>
    </div>
  );
};

const StatCard = ({ label, value, tone='gray' }) => {
  const tones = { gray:'text-ink-700', blue:'text-brand-700', green:'text-emerald-600', amber:'text-amber-600' };
  return (
    <Card>
      <div className={`text-xl sm:text-2xl font-bold ${tones[tone]}`}>{value}</div>
      <div className="text-xs text-ink-500 mt-0.5">{label}</div>
    </Card>
  );
};

const StudentApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);
  const [responding, setResponding] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [reviewApp, setReviewApp] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/applications/mine').then(r => setApplications(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);


  const respondInvite = async (id, action) => {
    const verb = action === 'accept' ? 'kabul' : 'red';
    if (!window.confirm('Daveti ' + verb + ' etmek istiyor musun?')) return;
    setResponding(id);
    try {
      await studentAPI.respondInvitation(id, action);
      load();
    } catch (e) { alert(e.response?.data?.error || 'İşlem yapılamadı'); }
    setResponding(null);
  };

  const withdraw = async (id) => {
    if (!window.confirm('Başvuruyu geri çekmek istiyor musunuz?')) return;
    setWithdrawing(id);
    try { await api.delete(`/applications/${id}`); setApplications(a => a.filter(x => x.id !== id)); }
    catch (e) { alert(e.response?.data?.error || 'Geri çekilemedi'); }
    setWithdrawing(null);
  };

  return (
    <Layout>
      <PageHeader title="Başvurularım" description="Yaptığın tüm başvurular ve güncel durumları" />

      {applications.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Toplam"   value={applications.length} tone="gray" />
          <StatCard label="Davet"    value={applications.filter(a => a.status === 'invited').length} tone="blue" />
          <StatCard label="Kabul"    value={applications.filter(a => a.status === 'accepted').length} tone="green" />
          <StatCard label="Bekleyen" value={applications.filter(a => a.status === 'pending').length} tone="amber" />
        </div>
      )}

      {loading && <p className="text-ink-500">Yükleniyor...</p>}
      {!loading && applications.length === 0 && (
        <div className="text-center py-16 text-ink-500">
          <div className="text-4xl mb-3">📋</div>
          <p className="font-medium">Henüz başvuru yok</p>
          <p className="text-sm mt-1">Eşleşmeler sayfasından ilanlara başvurabilirsiniz.</p>
        </div>
      )}

      <div className="space-y-3 max-w-4xl">
        {applications.map(a => {
          const st = STATUS[a.status] || STATUS.pending;
          const isExp = expanded === a.id;
          return (
            <Card key={a.id}>
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-11 h-11 rounded-xl bg-ink-100 flex items-center justify-center text-lg flex-shrink-0">🏢</div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm sm:text-base text-ink-900">{a.position_title}</div>
                      <div className="text-xs sm:text-sm text-ink-500 truncate">
                        {a.company_name}{a.company_city && ' • '+a.company_city}
                        {a.internship_type && ' • '+(a.internship_type === 'remote' ? 'Uzaktan' : a.internship_type === 'hybrid' ? 'Hibrit' : 'Ofisten')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.match_score != null && <Badge tone={a.match_score >= 75 ? 'green' : a.match_score >= 50 ? 'amber' : 'gray'}>%{Math.round(a.match_score)} uyum</Badge>}
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                    <div className="text-xs text-ink-400">
                      Başvuru: {new Date(a.applied_at).toLocaleDateString('tr-TR', { day:'numeric', month:'long', year:'numeric' })}
                      {a.start_date && ' • Başlangıç: '+new Date(a.start_date).toLocaleDateString('tr-TR')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => setExpanded(isExp ? null : a.id)}
                        className={`px-3 py-1 text-xs border rounded-md ${isExp ? 'bg-brand-50 border-brand-300 text-brand-700' : 'border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                        {isExp ? 'Geçmişi Gizle' : 'Durum Geçmişi'}
                      </button>
                      {a.status === 'invited' && (
                        <>
                          <button onClick={() => respondInvite(a.id, 'accept')} disabled={responding === a.id}
                            className="px-3 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-medium">
                            {responding === a.id ? '...' : '✓ Kabul Et'}
                          </button>
                          <button onClick={() => respondInvite(a.id, 'decline')} disabled={responding === a.id}
                            className="px-3 py-1 text-xs border border-red-300 text-red-600 rounded-md hover:bg-red-50">Reddet</button>
                        </>
                      )}
                      {a.status === 'accepted' && (
                        <button onClick={() => setReviewApp(a)} className="px-3 py-1 text-xs bg-amber-500 text-white rounded-md hover:bg-amber-600 font-medium">⭐ Değerlendir</button>
                      )}
                      {a.status === 'pending' && (
                        <button onClick={() => withdraw(a.id)} disabled={withdrawing === a.id}
                          className="px-3 py-1 text-xs border border-red-300 text-red-600 rounded-md hover:bg-red-50">
                          {withdrawing === a.id ? 'Geri çekiliyor...' : 'Geri Çek'}
                        </button>
                      )}
                    </div>
                  </div>

                  {a.interview_date && (
                    <div className="mt-2 text-sm text-brand-700 bg-brand-50 px-3 py-1.5 rounded-md inline-block">
                      Mülakat: {new Date(a.interview_date).toLocaleString('tr-TR', { day:'numeric', month:'long', hour:'2-digit', minute:'2-digit' })}
                    </div>
                  )}
                  {a.status === 'rejected' && a.rejection_reason && (
                    <div className="mt-2 text-sm text-red-700 bg-red-50 px-3 py-1.5 rounded-md">Gerekçe: {a.rejection_reason}</div>
                  )}
                </div>
              </div>
              {isExp && <Timeline appId={a.id} appliedAt={a.applied_at} />}
            </Card>
          );
        })}
      </div>
      {reviewApp && <ReviewModal app={reviewApp} onClose={() => setReviewApp(null)} />}
    </Layout>
  );
};

export default StudentApplications;
